import { Component, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { Session } from '../../services/session';
import { ApiEndpoints } from '../../core/api-endpoints';
import { FlashService } from '../../services/flash';
import { SchoolService } from '../../services/school';
import { ThemeService } from '../../services/theme';
import { ShimmerEffect } from '../../shared/shimmer-effect/shimmer-effect';

interface INavLink {
  id: number;
  route: string;
  routeName: string;
  imgSrc: string;
  imgSrcFilled: string;
  roles: string[];
}
@Component({
  selector: 'app-dashboard',
  imports: [RouterOutlet, RouterLinkActive, RouterLink, ShimmerEffect],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard {
  constructor(
    private router: Router,
    private session: Session,
    private schoolService: SchoolService,
    private themeService: ThemeService,
  ) {
    const token = this.session.getItem('token');

    if (!token) {
      void this.router.navigate(['/']);
      return;
    }

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.schoolId) {
      this.getSchoolById(this.schoolId);
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.mainElement()?.nativeElement.scrollTo({ top: 0, left: 0 });
      });
  }

  private readonly mainElement = viewChild<ElementRef<HTMLElement>>('main');

  protected isLoading = signal<boolean>(false);
  protected schoolId?: string;
  protected schoolName = signal('');
  protected schoolImage = signal('');
  protected readonly flashService = inject(FlashService);
  protected showNav = signal<boolean>(false);
  protected loggedUserRole?: string;

  protected navLinks: INavLink[] = [
    {
      id: 0,
      route: 'overview',
      routeName: 'Dashboard',
      imgSrc: 'dashboard-icon.svg',
      imgSrcFilled: 'dashboard-filled-icon.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'FINANCE_ADMIN', 'TEACHER'],
    },
    {
      id: 1,
      route: 'academic-management',
      routeName: 'Academic Setup',
      imgSrc: 'academic-mgt-icon.svg',
      imgSrcFilled: 'academic-mgt-filled-icon.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'FINANCE_ADMIN', 'TEACHER'],
    },
    {
      id: 2,
      route: 'class',
      routeName: 'The Class',
      imgSrc: 'class-icon.svg',
      imgSrcFilled: 'class-filled-icon.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'TEACHER'],
    },
    {
      id: 3,
      route: 'student-fees',
      routeName: 'Fees',
      imgSrc: 'cedi-logo.svg',
      imgSrcFilled: 'cedi-logo.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'FINANCE_ADMIN'],
    },
    {
      id: 4,
      route: 'user-management',
      routeName: 'User Management',
      imgSrc: 'user-icon.svg',
      imgSrcFilled: 'user-filled-icon.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN'],
    },
    {
      id: 5,
      route: 'audit-logs',
      routeName: 'Audit Logs',
      imgSrc: 'document-icon.svg',
      imgSrcFilled: 'document-icon.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN'],
    },
    {
      id: 6,
      route: 'school-profile',
      routeName: 'School Profile',
      imgSrc: 'school-profile.svg',
      imgSrcFilled: 'school-profile-filled.svg',
      roles: ['SUPER_ADMIN', 'SCHOOL_ADMIN'],
    },
    // {
    //   id: ,
    //   route: '',
    //   routeName: '',
    //   imgSrc: '',
    // imgSrcFilled: ''
    // },
  ];

  protected visibleTabs(userRole: string): INavLink[] {
    return this.navLinks.filter((link) => link.roles.includes(userRole));
  }

  protected getSchoolById(id: string): void {
    this.isLoading.set(true);

    this.schoolService.getSchoolById(`${ApiEndpoints.url}/school/${id}`).subscribe({
      next: (data) => {
        this.schoolName.set(data.payload.school_name);
        this.schoolImage.set(data.payload.school_img);

        if (data.payload.primary_color !== null && data.payload.secondary_color !== null) {
          this.themeService.setTheme(data.payload.primary_color, data.payload.secondary_color);
        }

        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Unable to load school data. Refresh the page');
      },
    });
  }
}
